/**
 * Cloud Operations — Deploy Observability tab.
 *
 * Two side-by-side panels per the unPeg reference:
 *   left:  Metrics & Tracing (backend, endpoint, OTLP, distributed tracing)
 *   right: Logging (aggregator, level, endpoint, structured JSON, audit forward)
 *
 * State lives on the parent DeployHomePage form; posted as part of the single
 * quick-deploy POST. Backend stashes into deployment.config_overrides.
 */

import React from 'react';
import { T, inp, Field } from './_ui.jsx';
import { Toggle } from './DeployConfigureTab.jsx';

export default function DeployObservabilityTab({ form, updateObs }) {
  const o = form.observability;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 16,
      alignItems: 'start',
    }}>
      <Panel icon="◆" title="Metrics & Tracing" accentBg={T.bgAlt}>
        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="Metrics Backend">
            <select
              style={inp}
              value={o.metrics_backend}
              onChange={(e) => updateObs({ metrics_backend: e.target.value })}
            >
              <option value="prometheus">prometheus</option>
              <option value="cloud-monitoring">GCP Cloud Monitoring</option>
              <option value="cloudwatch">AWS CloudWatch</option>
              <option value="azure-monitor">Azure Monitor</option>
              <option value="datadog">Datadog</option>
            </select>
          </Field>
          <Field label="Prometheus Endpoint">
            <input
              style={inp}
              value={o.metrics_endpoint}
              onChange={(e) => updateObs({ metrics_endpoint: e.target.value })}
            />
          </Field>
          <Field label="Tracing Backend">
            <select
              style={inp}
              value={o.tracing_backend}
              onChange={(e) => updateObs({ tracing_backend: e.target.value })}
            >
              <option value="jaeger">jaeger</option>
              <option value="tempo">tempo</option>
              <option value="zipkin">zipkin</option>
              <option value="cloud-trace">GCP Cloud Trace</option>
              <option value="xray">AWS X-Ray</option>
            </select>
          </Field>
          <Field label="OTLP Endpoint">
            <input
              style={inp}
              value={o.otlp_endpoint}
              onChange={(e) => updateObs({ otlp_endpoint: e.target.value })}
            />
          </Field>
        </div>
        <div style={{ marginTop: 12 }}>
          <Toggle
            first
            label="Distributed Tracing"
            hint="Propagate trace context"
            value={o.distributed_tracing}
            onChange={(v) => updateObs({ distributed_tracing: v })}
          />
        </div>
      </Panel>

      <Panel icon="▭" title="Logging" accentBg="#eef4ff">
        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="Log Aggregator">
            <select
              style={inp}
              value={o.log_aggregator}
              onChange={(e) => updateObs({ log_aggregator: e.target.value })}
            >
              <option value="elasticsearch">elasticsearch</option>
              <option value="loki">loki</option>
              <option value="cloud-logging">GCP Cloud Logging</option>
              <option value="cloudwatch-logs">AWS CloudWatch Logs</option>
              <option value="datadog">Datadog</option>
            </select>
          </Field>
          <Field label="Log Level">
            <select
              style={inp}
              value={o.log_level}
              onChange={(e) => updateObs({ log_level: e.target.value })}
            >
              <option value="trace">trace</option>
              <option value="debug">debug</option>
              <option value="info">info</option>
              <option value="warn">warn</option>
              <option value="error">error</option>
            </select>
          </Field>
          <Field label="Loki / ES Endpoint">
            <input
              style={inp}
              value={o.loki_es_endpoint}
              onChange={(e) => updateObs({ loki_es_endpoint: e.target.value })}
            />
          </Field>
        </div>
        <div style={{ marginTop: 12 }}>
          <Toggle
            first
            label="Structured JSON Logging"
            hint="Machine-readable log format"
            value={o.structured_json_logging}
            onChange={(v) => updateObs({ structured_json_logging: v })}
          />
          <Toggle
            label="Audit Log Forwarding"
            hint="Forward audit trail externally"
            value={o.audit_log_forwarding}
            onChange={(v) => updateObs({ audit_log_forwarding: v })}
          />
        </div>
      </Panel>
    </div>
  );
}

function Panel({ icon, title, accentBg, children }) {
  return (
    <div style={{
      background: T.bgCard,
      border: `1px solid ${T.stroke}`,
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      <div style={{
        background: accentBg,
        padding: '14px 18px',
        borderBottom: `1px solid ${T.stroke2}`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ width: 24, height: 24, color: T.ink, fontSize: 14, fontWeight: 700,
          display: 'grid', placeItems: 'center' }}>{icon}</span>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{title}</div>
      </div>
      <div style={{ padding: 18 }}>{children}</div>
    </div>
  );
}
